import React, { useState, useEffect } from "react";

/**
 * AdminCruiseSchedule.jsx
 * Simple admin panel for MSC Cruises:
 * - Arrivals: arrival time + city + Set + Modify
 * - Departures: departure time + city + Set + Modify
 * - Responsive: table on desktop, cards on mobile
 */

function pad2(n) {
  const s = String(n ?? "");
  return s.length === 1 ? `0${s}` : s;
}

function normalizeTime(v) {
  const raw = String(v || "").trim();
  if (!raw) return "";

  // If user types only hour: "9" or "09" or "10" => "09:00" / "10:00"
  if (/^\d{1,2}$/.test(raw)) {
    return `${pad2(raw)}:00`;
  }

  // If user types "930" or "0930" or "1230" => "09:30" / "12:30"
  if (/^\d{3,4}$/.test(raw)) {
    const hh = raw.length === 3 ? raw.slice(0, 1) : raw.slice(0, 2);
    const mm = raw.slice(-2);
    return `${pad2(hh)}:${pad2(mm)}`;
  }

  // If user types "9:5" or "9:05" or "09:5" => "09:05"
  const m = raw.match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);
  if (m) return `${pad2(m[1])}:${pad2(m[2])}`;

  // Fallback: keep first 5 chars
  return raw.slice(0, 5);
}

function clampTimeHHMM(v) {
  const t = normalizeTime(v);
  if (!t) return "";

  const m = t.match(/^(\d{2}):(\d{2})$/);
  if (!m) return "";

  const hh = Number(m[1]);
  const mm = Number(m[2]);

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return "";
  if (hh < 0 || hh > 23) return "";     // 24h rule
  if (mm < 0 || mm > 59) return "";

  return `${pad2(hh)}:${pad2(mm)}`;
}


function uid() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

function getBrowserPosition(options = {}) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("No window"));
    if (!("geolocation" in navigator)) return reject(new Error("Geolocation not supported"));

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: 9000,
        maximumAge: 30_000,
        ...options,
      }
    );
  });
}

function Modal({ open, title, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close"
      />
      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0B0F19] text-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="text-base sm:text-lg font-semibold">{title}</div>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm text-white/80 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            ✕
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function BoardShell({ title, children }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#0B0F19] to-[#070A12] shadow-[0_25px_70px_-35px_rgba(0,0,0,0.9)]">
      <div className="h-14 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-600">
        <div className="h-full px-5 flex items-center justify-between">
          <div className="text-[#1b1300] font-extrabold tracking-wide text-lg">
            {title}
          </div>
          <div className="text-[#1b1300] font-extrabold tracking-wide">
            MSC Admin
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

function DesktopTable({ rows, timeLabel, onSet, onModify, onAdd }) {
  return (
    <div className="hidden md:block">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm text-white/70">Manually set time and city.</div>
        <button
          onClick={onAdd}
          className="rounded-xl bg-amber-400/15 border border-amber-400/30 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          + Add row
        </button>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-white/5">
            <tr className="text-xs uppercase tracking-wider text-white/70">
              <th className="px-4 py-3">{timeLabel}</th>
              <th className="px-4 py-3">City</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-[#070A12]">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/10 hover:bg-white/5">
                <td className="px-4 py-3 font-mono text-amber-200 tracking-widest">
                  {r.time || "—"}
                </td>
                <td className="px-4 py-3">
                  <span className="text-amber-100 font-semibold tracking-wide">
                    {String(r.city || "").toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onSet(r)}
                      className="rounded-lg bg-amber-400/15 border border-amber-400/30 px-3 py-2 text-xs font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      Set
                    </button>
                    <button
                      onClick={() => onModify(r)}
                      className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-xs font-semibold text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      Modify
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-white/60">
                  No rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MobileCards({ rows, timeLabel, onSet, onModify, onAdd }) {
  return (
    <div className="md:hidden">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-sm text-white/70">Manually set time and city.</div>
        <button
          onClick={onAdd}
          className="rounded-xl bg-amber-400/15 border border-amber-400/30 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          + Add
        </button>
      </div>

      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/60">{timeLabel}</div>
            <div className="mt-1 font-mono text-amber-200 tracking-widest text-lg">{r.time || "—"}</div>

            <div className="mt-3 text-[11px] uppercase tracking-wider text-white/60">City</div>
            <div className="mt-1 text-amber-100 font-semibold tracking-wide break-words">
              {String(r.city || "").toUpperCase()}
            </div>

            <div className="mt-4 flex gap-2">
              <button
                onClick={() => onSet(r)}
                className="flex-1 rounded-xl bg-amber-400/15 border border-amber-400/30 px-3 py-3 text-sm font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                Set
              </button>
              <button
                onClick={() => onModify(r)}
                className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-3 text-sm font-semibold text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                Modify
              </button>
            </div>
          </div>
        ))}

        {rows.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center text-white/60">
            No rows.
          </div>
        )}
      </div>
    </div>
  );
}

async function reverseToCity(lat, lng) {
  // We can replace this with a backend later (/api/geo/reverse)
  // This uses OpenStreetMap Nominatim directly (no key) — fine for prototypes.
  const url =
    `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}`;

  const r = await fetch(url, {
    headers: {
      "Accept": "application/json",
    },
  });

  if (!r.ok) throw new Error("Reverse geocode failed");
  const data = await r.json();

  const a = data?.address || {};
  const city =
    a.city || a.town || a.village || a.municipality || a.county || "";

  return { city, country: a.country || "" };
}

export default function AdminCruiseSchedule() {
  const [toast, setToast] = useState("");
  
  const [departures, setDepartures] = useState([
    { id: uid(), time: "08:20", city: "Naples" },
    { id: uid(), time: "10:30", city: "Palermo" },
  ]);

  const [arrivals, setArrivals] = useState([
    { id: uid(), time: "07:10", city: "Barcelona" },
    { id: uid(), time: "12:45", city: "Genoa" },
  ]);

  const [editing, setEditing] = useState(null);
  const [editingKind, setEditingKind] = useState("departures");
  const [draft, setDraft] = useState({ time: "", city: "" })

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(""), 1800);
  };

  const onAddRow = (kind) => {
    const row = { id: uid(), time: "09:00", city: "New City" };
    if (kind === "departures") setDepartures((p) => [...p, row]);
    else setArrivals((p) => [row, ...p]);

    // open modify immediately
    openModify(row, kind);
  };

  const openModify = (row, kind) => {
    setEditingKind(kind);
    setEditing(row);
    setDraft({
      time: normalizeTime(row.time),
      city: row.city || "",
    });
  };

const saveModify = () => {
  const fixedTime = clampTimeHHMM(draft.time);
  if (!fixedTime) {
    showToast("Invalid time. Use 00:00 to 23:59.");
    return;
  }

  const next = {
    ...editing,
    time: fixedTime,
    city: (draft.city || "").trim(),
  };

  if (editingKind === "departures") {
    setDepartures((prev) => prev.map((x) => (x.id === next.id ? next : x)));
  } else {
    setArrivals((prev) => prev.map((x) => (x.id === next.id ? next : x)));
  }

  setEditing(null);
  showToast("Saved.");
};


  const onSet = (row, kind) => {
    // For now: local “apply” action. Later: we call the backend.
    showToast(`${kind === "departures" ? "Departure" : "Arrival"} set: ${row.time} • ${row.city}`);
  };


  useEffect(() => {
  if (!editing) return; // modal closed

  let cancelled = false;

  (async () => {
    try {
      const { lat, lng } = await getBrowserPosition();
      const { city } = await reverseToCity(lat, lng);

      if (!cancelled && city) {
        // Always set city from GPS when modal opens
        setDraft((prev) => ({ ...prev, city }));
      }
    } catch {
      // silent fail: user can still type manually
    }
  })();

  return () => {
    cancelled = true;
  };
}, [editing]);


  return (
    <div className="min-h-screen bg-[#05070D] text-white">
      {/* background glow */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.16]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(255,193,7,0.18), transparent 40%), radial-gradient(circle at 80% 30%, rgba(56,189,248,0.16), transparent 45%)",
        }}
      />

      <div className="relative mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Admin Panel • Cruise Schedule
            </div>
            <h1 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
              MSC Cruises — Arrivals & Departures
            </h1>
            <p className="mt-1 text-white/65 max-w-2xl">
              Enter arrival/departure time and city manually. Use “Modify” to edit a row and “Set” to apply it.
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-6">
          <BoardShell title="Departures">
            <DesktopTable
              rows={departures}
              timeLabel="Dep. Time"
              onAdd={() => onAddRow("departures")}
              onSet={(r) => onSet(r, "departures")}
              onModify={(r) => openModify(r, "departures")}
            />
            <MobileCards
              rows={departures}
              timeLabel="Dep. Time"
              onAdd={() => onAddRow("departures")}
              onSet={(r) => onSet(r, "departures")}
              onModify={(r) => openModify(r, "departures")}
            />
          </BoardShell>

          <BoardShell title="Arrivals">
            <DesktopTable
              rows={arrivals}
              timeLabel="Arr. Time"
              onAdd={() => onAddRow("arrivals")}
              onSet={(r) => onSet(r, "arrivals")}
              onModify={(r) => openModify(r, "arrivals")}
            />
            <MobileCards
              rows={arrivals}
              timeLabel="Arr. Time"
              onAdd={() => onAddRow("arrivals")}
              onSet={(r) => onSet(r, "arrivals")}
              onModify={(r) => openModify(r, "arrivals")}
            />
          </BoardShell>
        </div>
      </div>

      {/* Toast */}
      {toast ? (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999]">
          <div className="rounded-full border border-white/10 bg-[#0B0F19]/95 px-4 py-2 text-sm text-white/90 shadow-xl">
            {toast}
          </div>
        </div>
      ) : null}

      {/* Modify modal */}
      <Modal
        open={!!editing}
        title="Modify row"
        onClose={() => setEditing(null)}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-xs uppercase tracking-wider text-white/60">
              {editingKind === "departures" ? "Dep. Time" : "Arr. Time"} (HH:MM)
            </div>
            <input
              value={draft.time}
              onChange={(e) => setDraft((p) => ({ ...p, time: e.target.value }))}
              onBlur={(e) => {const fixed = clampTimeHHMM(e.target.value); setDraft((p) => ({ ...p, time: fixed })); if (!fixed && e.target.value.trim()) showToast("Invalid time. Use 00:00 to 23:59.");}}
              className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 font-mono text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
              inputMode="numeric"
              placeholder="08:30"
            />
          </label>

        <label className="block">
        <div className="text-xs uppercase tracking-wider text-white/60">City</div>
        <input
            value={draft.city}
            onChange={(e) => setDraft((p) => ({ ...p, city: e.target.value }))}
            className="mt-1 w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
            placeholder="Naples"
        />
        </label>
        </div>

        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <button
            onClick={() => setEditing(null)}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white/85 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            Cancel
          </button>
          <button
            onClick={saveModify}
            className="rounded-xl bg-amber-400/15 border border-amber-400/30 px-4 py-2.5 text-sm font-semibold text-amber-200 hover:bg-amber-400/20 focus:outline-none focus:ring-2 focus:ring-amber-400"
          >
            Save
          </button>
        </div>
      </Modal>
    </div>
  );
}
